/**
 * WBS Kırınımları — Şantiye seçim ekranı.
 * ProjectMahalYapisi ile aynı desen; kullanıcı bir şantiye kartını tıklar,
 * `/proje/:siteId/wbs` sayfasına yönlendirilir.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSites } from "@/context/SiteContext";
import { searchMatchTR } from "@/lib/textFormat";
import {
  MapPin, MagnifyingGlass, CaretRight, TreeStructure,
} from "@phosphor-icons/react";

export default function WBSIndex() {
  const { sites } = useSites();
  const [q, setQ] = useState("");

  const visible = useMemo(() => {
    const active = (sites || []).filter((s) => s.status !== "arsiv");
    if (!q.trim()) return active;
    return active.filter((s) =>
      searchMatchTR(q, [s.code, s.name, s.city, s.district].filter(Boolean).join(" "))
    );
  }, [sites, q]);

  return (
    <Shell title="WBS Kırınımları" subtitle="Proje Yönetimi">
      <div className="space-y-6" data-testid="wbs-index-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#95C11F]/15 flex items-center justify-center">
              <TreeStructure size={22} weight="duotone" className="text-[#95C11F]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1B4776]">
                Şantiyeler
              </h1>
              <p className="text-sm text-slate-500">
                İş Kırınım Yapısını düzenlemek için bir şantiye seçin.
              </p>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Şantiye ara..."
              className="pl-9 w-72"
              data-testid="wbs-index-search"
            />
          </div>
        </div>

        {visible.length === 0 ? (
          <Card className="p-10 text-center text-slate-500" data-testid="wbs-index-empty">
            {q ? "Arama sonucu bulunamadı." : "Henüz aktif şantiye bulunmuyor."}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visible.map((s) => (
              <Link
                key={s.id}
                to={`/proje/${s.id}/wbs`}
                data-testid={`wbs-index-site-${s.id}`}
                className="group"
              >
                <Card className="p-5 hover:border-[#95C11F] hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-[#95C11F]/40 hover:border-l-[#95C11F]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono font-bold text-[#95C11F] tracking-wider uppercase">
                        {s.code}
                      </div>
                      <div className="mt-1 text-base font-bold text-[#0F172A] group-hover:text-[#1B4776] transition-colors line-clamp-2">
                        {s.name}
                      </div>
                      {(s.city || s.district) && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                          <MapPin size={13} weight="fill" className="text-[#1B4776]/60" />
                          <span>{[s.district, s.city].filter(Boolean).join(", ")}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2 rounded-full bg-slate-50 group-hover:bg-[#95C11F] transition-colors">
                      <CaretRight
                        size={16}
                        weight="bold"
                        className="text-slate-400 group-hover:text-white transition-colors"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <TreeStructure size={13} weight="bold" />
                      İş kalemi hiyerarşisi
                    </span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
